import React, { useEffect, useState } from "react";
import { Bookmark, Pencil, Trash2, X } from "lucide-react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import ConfirmModal from "../../components/ConfirmModal";
import { BACKEND_URL } from "../../config";
import TreatmentPresetEditor from "../../components/TreatmentPresetEditor";
import type { TreatmentPreset } from "../../utils/treatmentPresets";

type SuggestionCategory = "chief_complaint" | "allergic" | "on_examination" | "advice";

interface SuggestionRow {
  id: number;
  category: SuggestionCategory;
  text: string;
  treatment_template?: TreatmentPreset | null;
}

const tabs: Array<{ key: SuggestionCategory; label: string }> = [
  { key: "chief_complaint", label: "Chief Complaint" },
  { key: "allergic", label: "Allergic" },
  { key: "on_examination", label: "O/E" },
  { key: "advice", label: "Advice" },
];

const ClinicalSuggestionsConfig: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SuggestionCategory>("chief_complaint");
  const [rows, setRows] = useState<SuggestionRow[]>([]);
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteRow, setDeleteRow] = useState<SuggestionRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [presetRow, setPresetRow] = useState<SuggestionRow | null>(null);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });

  useEffect(() => {
    if (presetRow) document.getElementById("treatment-preset-editor")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [presetRow?.id]);

  const api = async (path: string, options: RequestInit = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
      ...options,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const loadRows = async (category = activeTab) => {
    setLoading(true);
    try {
      setRows(await api(`/api/clinical-text-suggestions?category=${category}`));
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load suggestions", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setText("");
    setEditingId(null);
    loadRows(activeTab);
    setPresetRow(null);
  }, [activeTab]);

  const saveSuggestion = async (e: React.FormEvent) => {
    e.preventDefault();
    const suggestionTexts = text
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (!suggestionTexts.length) {
      setModal({ open: true, title: "Missing text", message: "Enter suggestion text before saving." });
      return;
    }

    try {
      if (editingId) {
        await api(`/api/clinical-text-suggestions/${editingId}`, {
          method: "PUT",
          body: JSON.stringify({ category: activeTab, text: suggestionTexts[0] }),
        });
      } else {
        await Promise.all(
          suggestionTexts.map((suggestionText) =>
            api("/api/clinical-text-suggestions", {
              method: "POST",
              body: JSON.stringify({ category: activeTab, text: suggestionText }),
            })
          )
        );
      }
      setText("");
      setEditingId(null);
      await loadRows();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to save suggestion", message: err.message || "Please try again." });
    }
  };

  const startEdit = (row: SuggestionRow) => {
    setEditingId(row.id);
    setText(row.text);
  };

  const resetForm = () => {
    setEditingId(null);
    setText("");
  };

  const confirmDelete = async () => {
    if (!deleteRow) return;
    try {
      await api(`/api/clinical-text-suggestions/${deleteRow.id}`, { method: "DELETE" });
      if (editingId === deleteRow.id) resetForm();
      if (presetRow?.id === deleteRow.id) setPresetRow(null);
      setDeleteRow(null);
      await loadRows();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to delete suggestion", message: err.message || "Please try again." });
    }
  };

  return (
    <SidebarLayout title="Configuration" subtitle="Manage assisted clinical text for consultation fields.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />
      <ConfirmModal
        open={Boolean(deleteRow)}
        title="Delete suggestion"
        message={`Delete "${deleteRow?.text || "this suggestion"}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteRow(null)}
      />

      <section className="rounded border border-border-light bg-surface-light p-5">
        <div className="mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Clinical suggestion categories">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`rounded border px-4 py-2 text-sm font-semibold transition ${activeTab === tab.key ? "border-primary bg-primary text-white" : "border-border-light text-subtext-light hover:bg-primary/10 hover:text-primary"}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(320px,420px)_1fr]">
          <form onSubmit={saveSuggestion} className="rounded border border-border-light bg-background-light p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold">{editingId ? "Edit Suggestion" : "Add Suggestion"}</h2>
              {editingId && (
                <button type="button" onClick={resetForm} title="Cancel edit" className="rounded border border-border-light p-2 text-subtext-light hover:bg-primary/10 hover:text-primary">
                  <X size={18} />
                </button>
              )}
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="min-h-32 w-full rounded border border-border-light bg-white p-3"
              placeholder={editingId ? "Update this suggestion" : "Enter one suggestion per line"}
            />
            <div className="mt-4 flex justify-end">
              <button type="submit" className="rounded bg-primary px-5 py-2 font-semibold text-white">{editingId ? "Update" : "Add"}</button>
            </div>
          </form>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead className="bg-background-light text-xs uppercase text-subtext-light">
                <tr>
                  <th className="p-2">Suggestion</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={2} className="p-4 text-center text-subtext-light">Loading...</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="p-2 font-semibold">{row.text}
                      {row.treatment_template && <div className="text-xs font-normal text-primary">Bookmark · {row.treatment_template.medicines.length} medicines</div>}
                    </td>
                    <td className="p-2">
                      <div className="flex justify-end gap-2">
                        {activeTab === "chief_complaint" && <button type="button" onClick={() => setPresetRow(row)} className="inline-flex items-center gap-1 rounded p-2 text-primary hover:bg-primary/10" title="Configure treatment preset"><Bookmark size={16} />Treatment preset</button>}
                        <button type="button" onClick={() => startEdit(row)} title="Edit suggestion" className="rounded p-2 text-primary hover:bg-primary/10">
                          <Pencil size={16} />
                        </button>
                        <button type="button" onClick={() => setDeleteRow(row)} title="Delete suggestion" className="rounded p-2 text-alert-red hover:bg-alert-red/10">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && !rows.length && <tr><td colSpan={2} className="p-4 text-center text-subtext-light">No suggestions added yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        {presetRow && <TreatmentPresetEditor key={presetRow.id} complaint={presetRow.text} initial={presetRow.treatment_template || null}
          onClose={() => setPresetRow(null)}
          onSave={async (template) => {
            await api(`/api/clinical-text-suggestions/${presetRow.id}`, { method: "PUT", body: JSON.stringify({ treatment_template: template }) });
            setPresetRow(null);
            await loadRows();
          }} />}
      </section>
    </SidebarLayout>
  );
};

export default ClinicalSuggestionsConfig;
